import type { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import { API_BASE_URL } from "./consts";
import { getCookie } from "@/lib/getCookie";
import { setCookie } from "@/lib/setCookie";
import { AUTH_API } from "./auth/apiRoutes";

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request Interceptor
instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getCookie("accessToken"); // Fetch token dynamically
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    // Prevent infinite loop
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getCookie("refreshToken");

      if (!refreshToken) {
        window.location.href = "/";
        return Promise.reject(error);
      }

      // If refresh already in progress, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
          console.log("🚀 ~ failedQueue:", failedQueue);
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const res = await axios.post(`${API_BASE_URL}${AUTH_API}/refresh`, {
          refreshToken,
        });

        const newAccessToken = res.data.accessToken;

        setCookie("accessToken", newAccessToken);
        instance.defaults.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return instance(originalRequest); // 🔥 RETRY LAST FAILED API
      } catch (refreshError) {
        processQueue(refreshError, null);
        window.location.href = "/";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
